import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useI18n } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

export default function Apply() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { user, loading } = useAuth();
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", cover_letter: "" });

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { state: { from: `/apply/${id}` } }); return; }
    setForm((f) => ({ ...f, full_name: user.name || "", email: user.email || "" }));
    api.get(`/jobs/${id}`).then((r) => setJob(r.data)).catch(() => navigate("/jobs"));
  }, [id, user, loading, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/applications", { ...form, job_id: id });
      toast.success(t.apply.success);
      navigate("/dashboard/applications");
    } catch {
      toast.error(t.apply.error);
    } finally { setSubmitting(false); }
  };

  if (!job) return <div className="container-pad py-32 text-center text-slate-500">{t.common.loading}</div>;

  return (
    <div className="container-pad mx-auto max-w-[720px] py-16" data-testid="page-apply">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">{t.apply.title}</div>
      <h1 className="text-3xl font-extrabold text-navy">{job.title[lang] || job.title.en}</h1>
      <div className="text-slate-600 mt-1 mb-8">{job.company} · {job.location}</div>

      <form onSubmit={submit} className="bg-white rounded-[24px] p-6 md:p-8 border border-slate-100 shadow-soft space-y-5" data-testid="apply-form">
        <div>
          <Label htmlFor="full_name">{t.apply.fullName} *</Label>
          <Input id="full_name" required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1.5 rounded-xl" data-testid="apply-full-name" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="email">{t.apply.email} *</Label>
            <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-xl" data-testid="apply-email" />
          </div>
          <div>
            <Label htmlFor="phone">{t.apply.phone} *</Label>
            <Input id="phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-xl" data-testid="apply-phone" />
          </div>
        </div>
        <div>
          <Label htmlFor="cover">{t.apply.cover}</Label>
          <Textarea id="cover" rows={5} value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })} className="mt-1.5 rounded-xl" data-testid="apply-cover" />
        </div>
        <button type="submit" disabled={submitting} className="w-full px-6 py-4 rounded-full bg-brandred hover:bg-red-700 text-white font-semibold shadow-lg shadow-red-500/20 disabled:opacity-60" data-testid="apply-submit">
          {submitting ? t.common.loading : t.common.submit}
        </button>
      </form>
    </div>
  );
}
